import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonIcon,
} from '@ionic/react';
import { arrowBack, person } from 'ionicons/icons';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../hooks/useAuth';
import { MessageList } from '../components/MessageList';
import { Chat } from '../components/Chat';
import { BaseUser, Company, Contractor, Ambassador } from '../types/user';
import '../theme/chat.css';

export const Messages: React.FC = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const location = useLocation();
  const history = useHistory();
  const [selectedChat, setSelectedChat] = useState<{
    conversationId: string;
    otherUser: BaseUser;
  } | null>(null);

  useEffect(() => {
    const handleIonViewWillEnter = () => {
      if (location.pathname === '/messages' && !location.search) {
        setSelectedChat(null);
      }
    };

    window.addEventListener('ionViewWillEnter', handleIonViewWillEnter);
    return () => {
      window.removeEventListener('ionViewWillEnter', handleIonViewWillEnter);
    };
  }, [location]);

  // Add a class to the body when chat is open to handle Samsung navigation bar
  useEffect(() => {
    if (selectedChat) {
      document.body.classList.add('chat-open');
    } else {
      document.body.classList.remove('chat-open');
    }

    return () => {
      document.body.classList.remove('chat-open');
    };
  }, [selectedChat]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const conversationId = searchParams.get('conversation');
    
    if (!conversationId) {
      setSelectedChat(null);
      return;
    }

    if (conversationId && currentUser?.uid) {
      const fetchConversation = async () => {
        try {
          const conversationDoc = await getDoc(doc(db, 'conversations', conversationId));
          if (conversationDoc.exists()) {
            const data = conversationDoc.data();
            // Fetch other user's data
            const otherUserId = data.participants.find(id => id !== currentUser?.uid);
            
            if (otherUserId && currentUser?.uid) {
              const userDoc = await getDoc(doc(db, 'users', otherUserId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                console.log('Other user data:', { ...userData, id: otherUserId });
                setSelectedChat({
                  conversationId,
                  otherUser: { ...userData, id: otherUserId } as BaseUser
                });
              }
            } else {
              console.error('Could not determine other user ID:', { 
                participants: data.participants, 
                currentUserId: currentUser?.uid 
              });
            }
          }
        } catch (error) {
          console.error('Error fetching conversation:', error);
        }
      };
      
      fetchConversation();
    }
  }, [location.search, currentUser?.uid]);

  const getName = (user: BaseUser) => {
    if (user.type === 'company') {
      const name = (user as Company).companyName;
      return name.length > 10 ? `${name.substring(0, 10)}...` : name;
    } else {
      const name = `${(user as Contractor | Ambassador).firstName} ${(user as Contractor | Ambassador).lastName}`;
      return name.length > 10 ? `${name.substring(0, 10)}...` : name;
    }
  };

  return (
    <IonPage>
      {selectedChat ? (
        <>
          <IonHeader className="ion-no-border">
            <IonToolbar className="glass-effect" style={{ 
              '--background': 'transparent',
              '--border-width': '0',
              '--padding-top': '10px',
              '--padding-bottom': '10px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px'
              }}>
                <div 
                  style={{
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: 'rgba(0, 104, 185, 0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                  }}
                  onClick={() => {
                    setSelectedChat(null);
                    history.replace('/messages');
                  }}
                >
                  <IonIcon 
                    icon={arrowBack} 
                    style={{ 
                      fontSize: '20px', 
                      color: '#0068B9'
                    }} 
                  />
                </div>
                
                <div 
                  style={{
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#0068B9',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => {
                    console.log('Navigating to profile:', selectedChat.otherUser.id);
                    history.push(`/profile/${selectedChat.otherUser.id}`);
                  }}
                >
                  {selectedChat.otherUser.type === 'company' 
                    ? (selectedChat.otherUser as Company).companyName 
                    : `${(selectedChat.otherUser as Contractor | Ambassador).firstName} ${(selectedChat.otherUser as Contractor | Ambassador).lastName}`}
                </div>
                
                <div 
                  style={{
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    console.log('Navigating to profile from avatar:', selectedChat.otherUser.id);
                    history.push(`/profile/${selectedChat.otherUser.id}`);
                  }}
                >
                  {selectedChat.otherUser.profilePhotoUrl ? (
                    <img 
                      src={selectedChat.otherUser.profilePhotoUrl} 
                      alt="Profile" 
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #0068B9',
                        boxShadow: '0 4px 10px rgba(0, 104, 185, 0.2)'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #0068B9 0%, #29C6F3 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 10px rgba(0, 104, 185, 0.2)'
                    }}>
                      <IonIcon 
                        icon={person} 
                        style={{
                          fontSize: '24px',
                          color: 'white'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </IonToolbar>
          </IonHeader>
          <Chat
            conversationId={selectedChat.conversationId}
            otherUser={selectedChat.otherUser}
          />
        </>
      ) : (
        <>
          <IonHeader className="ion-no-border">
            <IonToolbar className="glass-effect" style={{ 
              '--background': 'transparent',
              '--border-width': '0',
              '--padding-top': '10px',
              '--padding-bottom': '10px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 16px'
              }}>
                <h2 style={{
                  margin: 0,
                  color: '#0068B9',
                  fontWeight: 700,
                  fontSize: '1.5rem'
                }}>
                  {t('messages.title')}
                </h2>
              </div>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <MessageList
              onSelectConversation={(conversationId, otherUser) =>
                setSelectedChat({ conversationId, otherUser })
              }
            />
          </IonContent>
        </>
      )}
    </IonPage>
  );
};
